import { type ReactNode } from "react";

import { BdsText } from "@buildertrend/components";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { DropdownCheckListCellEditor } from "commonComponents/entity/customField/utils/customFieldTypes/DropdownCheckListCellEditor";
import { MultiSelectFieldEditor } from "commonComponents/entity/customField/utils/customFieldTypes/MultiSelectFieldEditor";
import {
    getOptionTexts,
    parseCommaSeparatedIds,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

import {
    type CustomFieldAssociatedType,
    type DropdownCheckListCustomFieldDefinition,
    type DropdownCheckListCustomFieldValue,
} from "handlers";

export const DropdownCheckListCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    DropdownCheckListCustomFieldValue,
    DropdownCheckListCustomFieldDefinition
> = {
    getValue(field): string | null {
        const validIds = field.selectedOptionIds?.filter((id) => id > 0);
        return validIds?.length ? validIds.join(",") : "";
    },

    setValue(field, value): DropdownCheckListCustomFieldValue {
        const ids = value
            ? value
                  .split(",")
                  .map(Number)
                  .filter((n) => !isNaN(n))
            : [];
        return { ...field, selectedOptionIds: ids };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): DropdownCheckListCustomFieldValue {
        return {
            dataType: "DropDownCheckList",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            selectedOptionIds: value
                ? value
                      .split(",")
                      .map(Number)
                      .filter((n) => !isNaN(n))
                : [],
        };
    },

    compare(valueA: string | null, valueB: string | null, definition?): number {
        const idsA = valueA ? parseCommaSeparatedIds(valueA) : [];
        const idsB = valueB ? parseCommaSeparatedIds(valueB) : [];
        const textA = getOptionTexts(idsA, definition);
        const textB = getOptionTexts(idsB, definition);
        return textA.localeCompare(textB, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null, definition): ReactNode {
        if (!value) {
            return <BdsText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const ids = parseCommaSeparatedIds(value);
        if (ids.length === 0) {
            return <BdsText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        const displayText = getOptionTexts(ids, definition);
        return <BdsText size="normal-md" text={displayText} />;
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        _autoFocus?: boolean,
        definition?: DropdownCheckListCustomFieldDefinition,
        _field?: DropdownCheckListCustomFieldValue,
        formMode?: "always-editing" | "click-to-edit",
    ): ReactNode {
        return (
            <MultiSelectFieldEditor
                customFieldId={customFieldId}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                definition={definition}
                testId="dropdownCheckListCustomFieldEditor"
                formMode={formMode}
            />
        );
    },

    getAgGridCellEditor() {
        return DropdownCheckListCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
